import { useCallback, useState } from "react";

import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type {
    MessageType,
    StreamEvent,
} from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { ResultWithFullResponse } from "app/services/fetchService";

import { aiAgentQueryKeys } from "./queryKeyFactory";
import { useSSEStream } from "./useSSEStream";

const useSuperAgentQuery = ({
    message,
    threadId,
    questionId,
    onComplete,
    onThreadCreated,
    fetcher,
}: {
    message?: string;
    threadId?: string;
    questionId: number;
    onThreadCreated: (id: string) => void;
    onComplete?: VoidFunction;
    fetcher: (payload: { message?: string; threadId?: string }) => Promise<ResultWithFullResponse<unknown>>;
}) => {
    const [data, setData] = useState("");
    const [messageId, setId] = useState("");
    const [messageType, setMessageType] = useState<MessageType>("ai");

    const handleComplete = () => {
        onComplete?.();
        setData("");
        void quotaQueryClient.invalidateDataCreditsState();
    };

    const onEvent = useCallback(
        (event: StreamEvent) => {
            if (ParseEventStreamService.isInfoStreamEvent(event)) {
                onThreadCreated(event.data.threadId);
            }

            if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
                setData((prev) => prev + event.data[0].content);
                if (event.data[0].id && event.data[0].id !== messageId) {
                    setId(event.data[0].id);
                }
                if (event.data[0].type && event.data[0].type !== messageType) {
                    setMessageType(event.data[0].type);
                }
            }
        },
        [messageId, messageType, onThreadCreated],
    );

    const { isFetching, stop, isFetched } = useSSEStream({
        enabled: !!message && salesSettingsHelper.isSuperAgentEnabled,
        queryKey: aiAgentQueryKeys.superAgent.chat(questionId.toString()),
        payload: { message, threadId },
        fetcher,
        onEvent,
        onComplete: handleComplete,
    });

    return { data, isLoading: isFetching, stop, messageId, isFetched, messageType };
};

export default useSuperAgentQuery;
